import { catchApiError, FieldViewModel, ICropping, IOmniaCommand, IOmniaShell, isArray, LocaleString, RoleId, str } from "../omnia";
import { CroppingDependencies, FieldDependencies, IFieldsModuleContext, IFieldsService } from "../types";

type Context = Omit<IFieldsModuleContext, "map" | "mapView">;

export class DeleteCmd implements IOmniaCommand {
    text: string = str.Delete;
    icon: string = "delete";

    constructor(private context: Context) {}

    canExecute(m: FieldViewModel): boolean {
        return !!(m && (m.field?.FieldId || m.cropping?.CroppingId));
    }

    execute(m: FieldViewModel) {
        const { shell, svc } = this.context;

        return catchApiError(shell, () =>
            m.isCropping() ? deleteCropping(shell, svc, m) : deleteField(shell, svc, m)
        );
    }

    isActive() {
        // We can't start a delete operation if we're currently editing.
        return !this.context?.editSvc?.isEditing;
    }
}


function deleteField(shell: IOmniaShell, svc: IFieldsService, field: FieldViewModel) {

    const admin = shell.getSession().currentUserRole <= RoleId.Admin

    return svc.canDeleteField(field.fieldId).then(deps => {

        const msgDeleteFieldConf = `Do you really want to delete field <strong>${field.getDescription(true)}</strong>?`;

        const p = deps
            ? showDeleteFieldError(shell, field, deps, admin)
            : omnia.shell.showDeleteCancel(shell, msgDeleteFieldConf)

        return p.then(r => {

            if (r) {
                return svc.deleteField(field.fieldId, deps && admin).then(() => true)
            }

            return false

        })

    })

}

function deleteCropping(shell: IOmniaShell, svc: IFieldsService, cropping: FieldViewModel) {

    const admin = shell.getSession().currentUserRole <= RoleId.Admin

    return svc.canDeleteCropping([cropping.croppingId]).then(r => {

        let deps = isArray(r) ? r[0] : undefined

        // This check is not performed by the API, only locally
        if (checkSuccessorInNutritionPlan(cropping)) {

            deps = {
                ...(deps || { CroppingId: cropping.croppingId }),
                HasSuccessorsInPlans: true
            }

        }

        const msgDeleteCroppingConf = `Do you really want to delete crop <strong>${cropping.getDescription(true)}</strong>?`;

        const p = checkCroppingDependencies(deps)
            ? showDeleteCroppingError(shell, cropping, deps, admin)
            : omnia.shell.showDeleteCancel(
                shell,
                msgDeleteCroppingConf
            ).then(ok => ok ? "realloc" : false)

        return p.then(ok => {

            return ok
                ? svc.deleteCropping(cropping.croppingId, deps && admin, ok).then(() => true)
                : false

        })

    })

}


function showDeleteFieldError(shell: IOmniaShell, field: FieldViewModel, deps: FieldDependencies, admin: boolean) {

    const msgCannotDeleteField = "This field cannot be deleted";

    const dependencies = [
        deps.HasCropping ? str.CroppingPlural : null,
        deps.HasLayers ? str.Layers : null,
        deps.HasSoilAnalysis ? str.SoilAnalysis : null,
        deps.HasPcnAnalysis ? str.Entities.PcnAnalysis : null,
        deps.HasHealthySoilAssements ? str.Entities.HSAPlural : null,
        deps.HasPublishedSprayPlans ? "Published spray plans" as LocaleString : null,
        deps.HasTasks ? "Tasks" : null
    ].filter(d => !!d)

    const args = {
        dependencies,
        title: msgCannotDeleteField,
        name: field.description,
        admin
    }

    return shell.showMessageBox({
        templateUrl: "app/fields/delete/field.html",
        arguments: args
    })

}

function showDeleteCroppingError(shell: IOmniaShell, cropping: FieldViewModel, deps: CroppingDependencies, admin: boolean) {

    const dependencies = [
        deps.HasNitrogenAnalysis ? str.Entities.NAnalysis : null,
        deps.HasFieldPerformanceData ? str.Entities.FieldPerf : null,
        deps.HasSuccessorsInPlans ? "Successors in nutrition plans" as LocaleString : null,
        deps.HasPublishedSprayPlans ? "Published spray plans" as LocaleString : null,
        deps.HasTasks ? "Tasks" : null
    ].filter(t => !!t)


    const layers = deps.HasLayers
    const otherDependencies = dependencies.length

    const msgCannotDeleteCrop = "This cropping cannot be deleted";
    const msgDeleteCrop = "Delete a cropping";

    const args = {
        dependencies,
        name: cropping.description,
        admin,
        layers,
        otherDependencies,
        canContinue: admin || (!otherDependencies),
        layerHandling: (layers ? undefined : "delete") as ("delete" | "realloc"),
        title: otherDependencies ? msgCannotDeleteCrop : msgDeleteCrop
    }

    return shell.showMessageBox({
        templateUrl: "app/fields/delete/cropping.html",
        arguments: args
    }).then(ok => ok ? args.layerHandling : false)
}


/**
 * Checks if a cropping as significant dependencies
 * @param deps Dependencies
 */
function checkCroppingDependencies(deps: CroppingDependencies): boolean {

    return deps && (
        deps.HasFieldPerformanceData
        || deps.HasNitrogenAnalysis
        || deps.HasLayers
        || deps.HasSuccessorsInPlans
        || deps.HasPublishedSprayPlans
        || deps.HasTasks
    )

}

/**
 * True if the cropping has at least one successor (next crop) in a nutrition plan
 * @param cropping Cropping
 */
function checkSuccessorInNutritionPlan(cropping: FieldViewModel): boolean {

    if (cropping.uncropped) {
        return false
    }

    function check(c: ICropping) {

        if (c && c.NextCroppings) {

            if (c.NextCroppings.some(c => c.InNutritionPlan)) {
                return true;
            }

            if (c.NextCroppings.some(n => check(n))) {
                return true;
            }

        }

    }

    return check(cropping.cropping)

}
